import React, { useState } from 'react';
import { Plus, Minus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { debtorService } from '@/lib/api';

const toUpperCase = (value) => value ? value.toUpperCase() : '';

const DebtorCreateDialog = ({ portfolios, customDebtorFields, onSuccess, children }) => {
  const [open, setOpen] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [newDebtor, setNewDebtor] = useState({
    name: '', dni: '', cuit: '',
    client_codes: [''],
    domains: [{ plate: '', actas: [{ number: '', infraccion: '', fecha: '', hora: '', monto: '', monto_quita: '', fecha_vencimiento_quita: '' }] }],
    email: '', phone: '', debt_amount: '', discount_amount: '', debt_date: '', portfolio_id: ''
  });

  const resetForm = () => {
    setNewDebtor({
      name: '', dni: '', cuit: '',
      client_codes: [''],
      domains: [{ plate: '', actas: [{ number: '', infraccion: '', fecha: '', hora: '', monto: '', monto_quita: '', fecha_vencimiento_quita: '' }] }],
      email: '', phone: '', debt_amount: '', discount_amount: '', debt_date: '', portfolio_id: ''
    });
    setInvoiceFile(null);
  };

  // Client code handlers
  const addClientCode = () => setNewDebtor({ ...newDebtor, client_codes: [...(newDebtor.client_codes || ['']), ''] });
  const updateClientCode = (index, value) => {
    const codes = [...(newDebtor.client_codes || [''])];
    codes[index] = toUpperCase(value);
    setNewDebtor({ ...newDebtor, client_codes: codes });
  };
  const removeClientCode = (index) => {
    const codes = newDebtor.client_codes?.filter((_, i) => i !== index) || [''];
    setNewDebtor({ ...newDebtor, client_codes: codes.length > 0 ? codes : [''] });
  };

  // Domain handlers
  const emptyDomain = { plate: '', actas: [{ number: '', infraccion: '', fecha: '', hora: '' }] };
  const addDomain = () => setNewDebtor({ ...newDebtor, domains: [...(newDebtor.domains || []), { ...emptyDomain }] });
  const updateDomain = (index, value) => {
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 7);
    const domains = [...(newDebtor.domains || [emptyDomain])];
    domains[index] = { ...domains[index], plate: cleaned };
    setNewDebtor({ ...newDebtor, domains });
  };
  const removeDomain = (index) => {
    const domains = newDebtor.domains?.filter((_, i) => i !== index) || [];
    setNewDebtor({ ...newDebtor, domains: domains.length > 0 ? domains : [{ ...emptyDomain }] });
  };

  // Acta handlers (each acta has number + infraccion)
  const addActa = (domainIndex) => {
    const domains = [...(newDebtor.domains || [])];
    domains[domainIndex].actas = [...(domains[domainIndex].actas || []), { number: '', infraccion: '', fecha: '', hora: '', monto: '', monto_quita: '', fecha_vencimiento_quita: '' }];
    setNewDebtor({ ...newDebtor, domains });
  };
  const updateActa = (domainIndex, actaIndex, field, value) => {
    const domains = [...(newDebtor.domains || [])];
    domains[domainIndex].actas[actaIndex] = { ...domains[domainIndex].actas[actaIndex], [field]: field === 'number' ? toUpperCase(value) : value };
    setNewDebtor({ ...newDebtor, domains });
  };
  const removeActa = (domainIndex, actaIndex) => {
    const domains = [...(newDebtor.domains || [])];
    domains[domainIndex].actas = domains[domainIndex].actas?.filter((_, i) => i !== actaIndex) || [];
    if (domains[domainIndex].actas.length === 0) domains[domainIndex].actas = [{ number: '', infraccion: '', fecha: '', hora: '', monto: '', monto_quita: '', fecha_vencimiento_quita: '' }];
    setNewDebtor({ ...newDebtor, domains });
  };

  const handleCreate = async () => {
    if (!newDebtor.dni && !newDebtor.cuit && !newDebtor.client_codes?.some(c => c.trim()) && !newDebtor.domains?.some(d => d.plate?.trim())) {
      toast.error('Debe proporcionar al menos DNI, CUIT, Codigo de Cliente o Dominio');
      return;
    }
    if (!newDebtor.name.trim()) { toast.error('El nombre es obligatorio'); return; }

    // Validate acta quita rules
    for (const domain of (newDebtor.domains || [])) {
      for (const acta of (domain.actas || [])) {
        if (acta.monto_quita && !acta.monto) {
          toast.error(`Acta ${acta.number || '?'}: Si ingresa Monto c/Quita, debe ingresar el Monto original`);
          return;
        }
        if (acta.monto_quita && !acta.fecha_vencimiento_quita) {
          toast.error(`Acta ${acta.number || '?'}: Si ingresa Monto c/Quita, debe ingresar la Fecha Vto. Quita`);
          return;
        }
      }
    }

    try {
      const customFields = {};
      Object.keys(newDebtor).forEach(key => {
        if (key.startsWith('custom_') && newDebtor[key]) customFields[key.replace('custom_', '')] = newDebtor[key];
      });

      const clientCodes = newDebtor.client_codes?.filter(c => c.trim()) || [];
      const domains = newDebtor.domains?.filter(d => d.plate?.trim()).map(d => ({
        plate: d.plate,
        actas: d.actas?.filter(a => a.number?.trim()).map(a => ({
          number: a.number,
          infraccion: a.infraccion || '',
          fecha: a.fecha || null,
          hora: a.hora || null,
          monto: a.monto ? parseFloat(a.monto) : null,
          monto_quita: a.monto_quita ? parseFloat(a.monto_quita) : null,
          discount_expiry: a.fecha_vencimiento_quita || null,
          monto_expiry: a.fecha_vencimiento_monto || null,
        })) || []
      })) || [];

      const payload = {
        dni: newDebtor.dni || null, cuit: newDebtor.cuit || null,
        client_codes: clientCodes.length > 0 ? clientCodes : null,
        client_code: clientCodes[0] || null,
        domains: domains.length > 0 ? domains : null,
        plate: domains[0]?.plate || null,
        name: newDebtor.name, email: newDebtor.email || null, phone: newDebtor.phone || null,
        debt_amount: newDebtor.debt_amount ? parseFloat(newDebtor.debt_amount) : null,
        discount_amount: newDebtor.discount_amount ? parseFloat(newDebtor.discount_amount) : null,
        debt_date: newDebtor.debt_date || null, portfolio_id: newDebtor.portfolio_id || null,
        custom_fields: Object.keys(customFields).length > 0 ? customFields : null
      };

      const response = await debtorService.createDebtor(payload);
      const debtorId = response.data?.id;

      if (invoiceFile && debtorId && newDebtor.portfolio_id) {
        try {
          await debtorService.uploadInvoice(newDebtor.portfolio_id, debtorId, invoiceFile);
          toast.success('Cliente y factura creados exitosamente');
        } catch { toast.warning('Cliente creado pero hubo un error al subir la factura'); }
      } else {
        toast.success('Cliente creado exitosamente');
      }

      setOpen(false);
      resetForm();
      onSuccess?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : detail || 'Error al crear cliente');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent data-testid="create-debtor-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Crear Nuevo Cliente</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800"><strong>Obligatorio:</strong> Al menos uno de DNI, CUIT, Codigo de Cliente o Dominio</p>
          </div>

          <div>
            <Label>Cartera (opcional)</Label>
            <Select value={newDebtor.portfolio_id || "none"} onValueChange={(v) => setNewDebtor({ ...newDebtor, portfolio_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="debtor-portfolio-select"><SelectValue placeholder="Sin cartera asignada" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin cartera</SelectItem>
                {portfolios.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Nombre Completo *</Label>
            <Input data-testid="debtor-name-input" value={newDebtor.name} onChange={(e) => setNewDebtor({ ...newDebtor, name: toUpperCase(e.target.value) })} placeholder="JUAN PEREZ" className="uppercase" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>DNI</Label><Input data-testid="debtor-dni-input" value={newDebtor.dni} onChange={(e) => setNewDebtor({ ...newDebtor, dni: toUpperCase(e.target.value) })} placeholder="12345678" className="uppercase" /></div>
            <div><Label>CUIT</Label><Input data-testid="debtor-cuit-input" value={newDebtor.cuit} onChange={(e) => setNewDebtor({ ...newDebtor, cuit: toUpperCase(e.target.value) })} placeholder="20-12345678-9" className="uppercase" /></div>
          </div>

          {/* Client Codes */}
          <div className="border rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Codigos de Cliente</Label>
              <Button type="button" variant="outline" size="sm" onClick={addClientCode} className="h-7 px-2"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-2">
              {(newDebtor.client_codes || ['']).map((code, index) => (
                <div key={index} className="flex gap-2">
                  <Input value={code} onChange={(e) => updateClientCode(index, e.target.value)} placeholder="CLI-001" className="uppercase flex-1" />
                  {(newDebtor.client_codes?.length || 1) > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeClientCode(index)} className="h-9 px-2 text-red-600 hover:text-red-700"><Minus className="w-4 h-4" /></Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Domains */}
          <div className="border rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Dominios</Label>
              <Button type="button" variant="outline" size="sm" onClick={addDomain} className="h-7 px-2"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-3">
              {(newDebtor.domains || [{ plate: '', actas: [{ number: '', infraccion: '' }] }]).map((domain, di) => (
                <div key={di} className="border rounded p-2 bg-white">
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={domain.plate}
                      onChange={(e) => updateDomain(di, e.target.value)}
                      placeholder="AD558AD"
                      className="uppercase flex-1"
                      maxLength={7}
                      data-testid={`domain-plate-${di}`}
                    />
                    {(newDebtor.domains?.length || 1) > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeDomain(di)} className="h-9 px-2 text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-2 pl-1">Solo letras y números, máx. 7 caracteres</p>
                  <div className="pl-3 border-l-2 border-slate-200">
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs text-slate-600">Actas</Label>
                      <Button type="button" variant="ghost" size="sm" onClick={() => addActa(di)} className="h-6 px-1 text-xs"><Plus className="w-3 h-3 mr-1" /> Acta</Button>
                    </div>
                    <div className="space-y-2">
                      {(domain.actas || [{ number: '', infraccion: '', fecha: '', hora: '' }]).map((acta, ai) => (
                        <div key={ai} className="space-y-1 bg-slate-50 rounded p-2">
                          <div className="flex gap-1">
                            <Input value={acta.number} onChange={(e) => updateActa(di, ai, 'number', e.target.value)} placeholder="N° Acta" className="uppercase h-8 text-sm flex-1" data-testid={`acta-number-${di}-${ai}`} />
                            {(domain.actas?.length || 1) > 1 && (
                              <Button type="button" variant="ghost" size="sm" onClick={() => removeActa(di, ai)} className="h-8 px-1 text-red-600 hover:text-red-700"><Minus className="w-3 h-3" /></Button>
                            )}
                          </div>
                          <Input value={acta.infraccion} onChange={(e) => updateActa(di, ai, 'infraccion', e.target.value)} placeholder="Infracción (ej: Estacionamiento prohibido)" className="h-8 text-sm" data-testid={`acta-infraccion-${di}-${ai}`} />
                          <div className="flex gap-1">
                            <Input type="date" value={acta.fecha || ''} onChange={(e) => updateActa(di, ai, 'fecha', e.target.value)} className="h-8 text-sm flex-1" data-testid={`acta-fecha-${di}-${ai}`} />
                            <Input type="time" value={acta.hora || ''} onChange={(e) => updateActa(di, ai, 'hora', e.target.value)} className="h-8 text-sm w-28" placeholder="HH:MM" data-testid={`acta-hora-${di}-${ai}`} />
                          </div>
                          <div className="flex gap-1">
                            <Input type="number" value={acta.monto || ''} onChange={(e) => updateActa(di, ai, 'monto', e.target.value)} placeholder={acta.monto_quita ? 'Monto $ (requerido)' : 'Monto $'} className={`h-8 text-sm flex-1 ${acta.monto_quita && !acta.monto ? 'border-red-400 ring-1 ring-red-300' : ''}`} data-testid={`acta-monto-${di}-${ai}`} />
                            <Input type="number" value={acta.monto_quita || ''} onChange={(e) => updateActa(di, ai, 'monto_quita', e.target.value)} placeholder="C/Quita $" className="h-8 text-sm flex-1" data-testid={`acta-monto-quita-${di}-${ai}`} />
                          </div>
                          <div className="flex gap-1">
                            <div className="flex-1">
                              <Label className="text-xs text-slate-500">Vto. Monto</Label>
                              <Input type="date" value={acta.fecha_vencimiento_monto || ''} onChange={(e) => updateActa(di, ai, 'fecha_vencimiento_monto', e.target.value)} className="h-8 text-sm" data-testid={`acta-vto-monto-${di}-${ai}`} />
                            </div>
                            <div className="flex-1">
                              <Label className={`text-xs ${acta.monto_quita && !acta.fecha_vencimiento_quita ? 'text-red-500 font-medium' : 'text-slate-500'}`}>Vto. Quita {acta.monto_quita ? '*' : ''}</Label>
                              <Input type="date" value={acta.fecha_vencimiento_quita || ''} onChange={(e) => updateActa(di, ai, 'fecha_vencimiento_quita', e.target.value)} className={`h-8 text-sm ${acta.monto_quita && !acta.fecha_vencimiento_quita ? 'border-red-400 ring-1 ring-red-300' : ''}`} data-testid={`acta-vto-quita-${di}-${ai}`} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input data-testid="debtor-email-input" type="email" value={newDebtor.email} onChange={(e) => setNewDebtor({ ...newDebtor, email: e.target.value.toLowerCase() })} placeholder="juan@email.com" /></div>
            <div><Label>Telefono</Label><Input data-testid="debtor-phone-input" value={newDebtor.phone} onChange={(e) => setNewDebtor({ ...newDebtor, phone: e.target.value })} placeholder="+54 11 1234-5678" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Monto Deuda</Label><Input data-testid="debtor-debt-input" type="number" value={newDebtor.debt_amount} onChange={(e) => setNewDebtor({ ...newDebtor, debt_amount: e.target.value })} placeholder="10000" /></div>
            <div><Label>Monto c/Quita</Label><Input data-testid="debtor-discount-input" type="number" value={newDebtor.discount_amount} onChange={(e) => setNewDebtor({ ...newDebtor, discount_amount: e.target.value })} placeholder="8000" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Fecha de Deuda</Label><Input data-testid="debtor-date-input" type="date" value={newDebtor.debt_date} onChange={(e) => setNewDebtor({ ...newDebtor, debt_date: e.target.value })} /></div>
          </div>

          {/* Custom Fields */}
          {customDebtorFields.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">Campos Personalizados</Label>
              <div className="grid grid-cols-2 gap-3">
                {customDebtorFields.map(field => (
                  <div key={field.field_id}>
                    <Label className="text-sm">{field.field_name}{field.is_required && <span className="text-red-500 ml-1">*</span>}</Label>
                    {field.field_type === 'select' && field.options?.length > 0 ? (
                      <Select value={newDebtor[`custom_${field.field_id}`] || ''} onValueChange={(v) => setNewDebtor({ ...newDebtor, [`custom_${field.field_id}`]: v })}>
                        <SelectTrigger><SelectValue placeholder={`Seleccionar ${field.field_name}`} /></SelectTrigger>
                        <SelectContent>{field.options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'} value={newDebtor[`custom_${field.field_id}`] || ''} onChange={(e) => setNewDebtor({ ...newDebtor, [`custom_${field.field_id}`]: e.target.value })} placeholder={field.field_name} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invoice Upload */}
          <div>
            <Label>Factura / Documento (opcional)</Label>
            <div className="mt-1">
              <Input data-testid="debtor-invoice-input" type="file" accept="image/*,application/pdf" onChange={(e) => setInvoiceFile(e.target.files[0] || null)} className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100" />
              {invoiceFile && <p className="mt-1 text-xs text-slate-500">Archivo seleccionado: {invoiceFile.name}</p>}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setOpen(false); resetForm(); }} className="flex-1">Cancelar</Button>
            <Button data-testid="submit-create-debtor-button" onClick={handleCreate} className="flex-1 bg-teal-600 hover:bg-teal-700">Crear Cliente</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DebtorCreateDialog;
